import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { ContactInput, Contact, Etiqueta } from '../types';
import { apiService } from '../services/api';
import { isValidPhone } from '../utils/phoneUtils';
import { useTheme } from '../contexts/ThemeContext';
import { PhoneInputWithDDI } from './PhoneInputWithDDI';

interface Category {
  id: string;
  nome: string;
}

const contactSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  telefone: z.string().min(1, 'Telefone é obrigatório').refine(isValidPhone, {
    message: 'Formato de telefone inválido',
  }),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  observacoes: z.string().optional(),
  categoriaId: z.string().optional(),
  tags: z.string().optional(),
});

type ContactFormData = z.infer<typeof contactSchema>;

interface ContactFormProps {
  contact?: Contact;
  onSuccess: () => void;
  onCancel: () => void;
}

export function ContactForm({ contact, onSuccess, onCancel }: ContactFormProps) {
  const { theme } = useTheme();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [etiquetas, setEtiquetas] = useState<Etiqueta[]>([]);
  const [loadingEtiquetas, setLoadingEtiquetas] = useState(true);
  const [selectedEtiquetas, setSelectedEtiquetas] = useState<Etiqueta[]>([]);
  const [phoneErrorModal, setPhoneErrorModal] = useState<{ isOpen: boolean; number: string; message: string } | null>(null);

  const {
    register,
    handleSubmit,
    control,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      nome: contact?.nome || '',
      telefone: contact?.telefone || '',
      email: contact?.email || '',
      observacoes: contact?.observacoes || '',
      categoriaId: contact?.categoriaId || '',
      tags: '',
    },
  });

  useEffect(() => {
    loadCategories();
    loadEtiquetas();
  }, []);

  // Carregar etiquetas selecionadas quando as etiquetas da API forem carregadas
  useEffect(() => {
    if (contact?.tags && contact.tags.length > 0 && etiquetas.length > 0 && selectedEtiquetas.length === 0) {
      loadSelectedEtiquetas(contact.tags);
    }
  }, [etiquetas, contact]);

  const loadCategories = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/categorias', {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadEtiquetas = async () => {
    try {
      setLoadingEtiquetas(true);
      
      // Buscar dados do tenant
      const tenant = await apiService.getCurrentTenant();
      
      if (!tenant.plataforma || !tenant.idPlataforma) {
        console.warn('Tenant não possui plataforma ou ID da plataforma configurados');
        setEtiquetas([]);
        return;
      }

      // Buscar etiquetas da API externa
      const response = await apiService.getEtiquetas(tenant.plataforma, tenant.idPlataforma);
      setEtiquetas(response.etiquetas || []);
    } catch (error) {
      console.error('Erro ao carregar etiquetas:', error);
      toast.error('Não foi possível carregar as etiquetas');
      setEtiquetas([]);
    } finally {
      setLoadingEtiquetas(false);
    }
  };

  const loadSelectedEtiquetas = (tags: string[]) => {
    // Carregar etiquetas selecionadas baseado nos títulos salvos nas tags
    const selected = etiquetas.filter(etiq => 
      tags.includes(etiq.title)
    );
    setSelectedEtiquetas(selected);
  };

  const handleAddEtiqueta = (etiqueta: Etiqueta) => {
    if (!selectedEtiquetas.find(e => e.id === etiqueta.id)) {
      setSelectedEtiquetas([...selectedEtiquetas, etiqueta]);
    }
  };

  const handleRemoveEtiqueta = (etiquetaId: number) => {
    setSelectedEtiquetas(selectedEtiquetas.filter(e => e.id !== etiquetaId));
  };

  const onSubmit = async (data: ContactFormData) => {
    try {
      // Converter etiquetas selecionadas em array de títulos
      const tagsArray = selectedEtiquetas.map(etiq => etiq.title);

      const contactInput: ContactInput = {
        nome: data.nome,
        telefone: data.telefone,
        email: data.email || undefined,
        observacoes: data.observacoes || undefined,
        categoriaId: data.categoriaId || undefined,
        tags: tagsArray.length > 0 ? tagsArray : undefined,
      };

      if (contact) {
        await apiService.updateContact(contact.id, contactInput);
        toast.success('Contato atualizado com sucesso');
      } else {
        await apiService.createContact(contactInput);
        toast.success('Contato criado com sucesso');
      }

      onSuccess();
    } catch (err: any) {
      // Verificar se é erro de quota
      if (err?.isQuotaError || err?.upgradeRequired) {
        toast.error(err.message || 'Limite de contatos atingido. Faça upgrade do seu plano para continuar.', {
          duration: 6000,
          icon: '⚠️'
        });
        return;
      }

      const errorMessage = err instanceof Error ? err.message : 'Erro ao salvar contato';
      
      // Se for erro de número de telefone inválido (do backend), abrir popup
      if (
        errorMessage.toLowerCase().includes('telefone inválido') || 
        errorMessage.toLowerCase().includes('phone number') ||
        errorMessage.toLowerCase().includes('inválido')
      ) {
        setPhoneErrorModal({
          isOpen: true,
          number: data.telefone,
          message: 'O número de telefone informado possui dígitos incorretos ou formatação inválida. Verifique se o DDD está correto e se não há dígitos a mais.'
        });
        return;
      }

      toast.error(errorMessage);
    }
  };

  const onInvalid = (formErrors: any) => {
    if (formErrors.telefone) {
      setPhoneErrorModal({
        isOpen: true,
        number: getValues('telefone') || '',
        message: 'O número de telefone informado possui dígitos incorretos ou formatação inválida. Verifique se o DDD está correto e se não há dígitos a mais.'
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999] backdrop-blur-sm">
      <div className="rounded-2xl shadow-2xl w-full max-w-lg border transition-colors max-h-[90vh] flex flex-col" style={{
        background: theme === 'dark' ? '#1D1E24' : 'var(--bg-card)',
        borderColor: 'var(--border-color)'
      }} role="dialog" aria-labelledby="form-title">
        <div className="text-center mb-8 px-8 pt-8 flex-shrink-0">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl mx-auto mb-4 flex items-center justify-center">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 id="form-title" className="text-xl font-semibold text-n-slate-12">
            {contact ? 'Editar Contato' : 'Novo Contato'}
          </h2>
          <p className="mt-2 text-sm font-normal text-n-slate-11">
            {contact ? 'Atualize as informações do contato' : 'Preencha os dados para criar um novo contato'}
          </p>
        </div>

        <div className="overflow-y-auto flex-1 px-8 pb-8">
          <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6">
          <div>
            <label htmlFor="nome" className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Nome *
            </label>
            <input
              id="nome"
              type="text"
              {...register('nome')}
              className="input-field"
              placeholder="Digite o nome completo"
            />
            {errors.nome && (
              <p className="text-theme-error text-xs font-normal mt-1">
                {errors.nome.message}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="telefone" className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Telefone *
            </label>
            <Controller
              name="telefone"
              control={control}
              render={({ field }) => (
                <PhoneInputWithDDI
              id="telefone"
                  value={field.value || ''}
                  onChange={field.onChange}
                  placeholder="Digite o número"
                  error={errors.telefone?.message}
                />
            )}
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Email
            </label>
            <input
              id="email"
              type="email"
              {...register('email')}
              className="input-field"
              placeholder="Digite o email"
            />
            {errors.email && (
              <p className="text-theme-error text-xs font-normal mt-1">
                {errors.email.message}
              </p>
            )}
          </div>


          <div>
            <label htmlFor="categoriaId" className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Categoria
            </label>
            <select
              id="categoriaId"
              {...register('categoriaId')}
              className="input-field"
              disabled={loadingCategories}
            >
              <option value="">Selecione uma categoria</option>
              {loadingCategories ? (
                <option disabled>Carregando categorias...</option>
              ) : (
                categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.nome}
                  </option>
                ))
              )}
            </select>
          </div>

          <div>
            <label htmlFor="etiquetas" className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Etiquetas
            </label>
            
            {/* Etiquetas selecionadas */}
            {selectedEtiquetas.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3 p-3 rounded-lg" style={{ 
                backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f3f4f6',
                borderColor: 'var(--border-color)',
                border: '1px solid'
              }}>
                {selectedEtiquetas.map((etiqueta) => (
                  <div
                    key={etiqueta.id}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
                    style={{
                      backgroundColor: theme === 'dark' ? 'rgba(74, 158, 255, 0.2)' : '#e0f2fe',
                      color: theme === 'dark' ? '#4a9eff' : '#0369a1',
                      border: `1px solid ${theme === 'dark' ? 'rgba(74, 158, 255, 0.3)' : '#7dd3fc'}`
                    }}
                  >
                    <span>{etiqueta.title}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveEtiqueta(etiqueta.id)}
                      className="hover:opacity-70 transition-opacity"
                      aria-label={`Remover etiqueta ${etiqueta.title}`}
                      title="Remover etiqueta"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Dropdown para adicionar etiquetas */}
            {loadingEtiquetas ? (
              <div className="input-field flex items-center justify-center py-3">
                <svg className="animate-spin h-5 w-5" style={{ color: 'var(--text-muted)' }} fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="ml-2 text-sm font-normal" style={{ color: 'var(--text-muted)' }}>Carregando etiquetas...</span>
              </div>
            ) : etiquetas.length === 0 ? (
              <div className="input-field py-3 text-center text-sm font-normal" style={{ color: 'var(--text-muted)' }}>
                Nenhuma etiqueta disponível. Configure a plataforma no cadastro da empresa.
              </div>
            ) : (
              <select
                id="etiquetas"
                className="input-field"
                onChange={(e) => {
                  const etiqueta = etiquetas.find(etiq => etiq.id.toString() === e.target.value);
                  if (etiqueta) {
                    handleAddEtiqueta(etiqueta);
                    e.target.value = ''; // Reset select
                  }
                }}
                defaultValue=""
              >
                <option value="">Selecione uma etiqueta para adicionar</option>
                {etiquetas
                  .filter(etiq => !selectedEtiquetas.find(selected => selected.id === etiq.id))
                  .map((etiqueta) => (
                    <option key={etiqueta.id} value={etiqueta.id}>
                      {etiqueta.title}
                    </option>
                  ))
                }
              </select>
            )}
            
            <p className="text-xs font-normal mt-2" style={{ color: 'var(--text-muted)' }}>
              Selecione etiquetas da sua plataforma para organizar seus contatos
            </p>
          </div>

          <div>
            <label htmlFor="observacoes" className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Observações
            </label>
            <textarea
              id="observacoes"
              {...register('observacoes')}
              rows={4}
              className="input-field resize-none"
              placeholder="Digite observações adicionais"
            />
          </div>

          <div className="flex gap-4 pt-6">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 py-3 px-6 rounded-xl font-medium transition-all duration-200 border"
              style={{
                backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f3f4f6',
                color: 'var(--text-primary)',
                borderColor: 'var(--border-color)'
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary flex-1 py-3 px-6 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-sm font-medium">Salvando...</span>
                </>
              ) : (
                'Salvar Contato'
              )}
            </button>
          </div>
        </form>
        </div>
      </div>
      
      {phoneErrorModal && phoneErrorModal.isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[10000] animate-fade-in">
          <div className="bg-white dark:bg-[#1D1E24] border border-red-500/20 rounded-2xl p-6 max-w-md w-full shadow-2xl animate-scale-in text-center" style={{
            background: theme === 'dark' ? '#1D1E24' : 'var(--bg-card)',
            borderColor: theme === 'dark' ? '#374151' : 'var(--border-color)'
          }}>
            <div className="w-16 h-16 bg-red-100 dark:bg-red-950/50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            
            <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
              Número de Telefone Inválido
            </h3>
            
            <p className="text-sm mb-4 leading-relaxed" style={{ color: 'var(--text-muted)' }}>
              {phoneErrorModal.message}
            </p>

            <div className="border rounded-xl p-3 mb-6 font-mono text-sm break-all font-semibold" style={{ 
              backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.2)' : '#f9fafb',
              borderColor: 'var(--border-color)',
              color: 'var(--text-primary)'
            }}>
              {phoneErrorModal.number}
            </div>

            <button
              type="button"
              onClick={() => setPhoneErrorModal(null)}
              className="w-full py-3 px-4 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Entendi, vou corrigir
            </button>
          </div>
        </div>
      )}
    </div>
  );
}